import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BeatLoader } from "react-spinners";
import { saveAs } from "file-saver";
import { LuEllipsisVertical, LuDownload } from "react-icons/lu";

import { Table } from "../../components/table/table";
import { getAllPostsByAdmin } from "../../services/postService";
import { IPost } from "../../types";
import ProjectDetailsModal from "../../components/modals/project-details";

interface ProjectsProps {
  searchQuery?: string;
}

const Projects: React.FC<ProjectsProps> = ({ searchQuery }) => {
  const { data, isLoading, error } = useQuery<{
    jobs: IPost[];
    projects: IPost[];
  }>({
    queryKey: ["allPostsByAdmin", searchQuery],
    queryFn: () => getAllPostsByAdmin(searchQuery),
  });

  const projects: IPost[] = data?.projects ?? [];
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<IPost | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openMenu &&
        !document
          .getElementById(`menu-${openMenu}`)
          ?.contains(event.target as Node)
      ) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenu]);

  const toggleMenu = (postId: string) => {
    setOpenMenu((prev) => (prev === postId ? null : postId));
  };

  const handleEdit = (id: string) => {
    const project = projects.find((project) => project._id === id);
    if (project) {
      setSelectedProject(project);
    }
    setOpenMenu(null);
  };

  const downloadCSV = () => {
    if (projects.length === 0) return;

    const csvContent = [
      [
        "Title",
        "Category",
        "Type",
        "Budget",
        "Experience",
        "Location",
        "Status",
      ],
      ...projects.map((project: IPost) => [
        project.title,
        project.category,
        project.postType,
        `$${project.salary.toLocaleString()}`,
        project.experience,
        `${project.state}, ${project.country}`,
        project.status,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "projects.csv");
  };

  const columns = [
    {
      header: "Title",
      accessorKey: "title",
      cell: ({ row }: { row: { original: IPost } }) => (
        <p className="text-sm text-gray-700 font-medium">
          {row.original.title}
        </p>
      ),
    },
    {
      header: "Category",
      accessorKey: "category",
      cell: ({ row }: { row: { original: IPost } }) => (
        <p className="text-sm text-gray-700">{row.original.category}</p>
      ),
    },
    {
      header: "Type",
      accessorKey: "postType",
      cell: ({ row }: { row: { original: IPost } }) => (
        <p className="capitalize text-sm text-gray-700">
          {row.original.postType}
        </p>
      ),
    },
    {
      header: "Budget (₦)",
      accessorKey: "salary",
      cell: ({ row }: { row: { original: IPost } }) => (
        <p className="text-sm text-gray-700">
          {row.original.salary.toLocaleString()}
        </p>
      ),
    },
    {
      header: "Location",
      accessorKey: "location",
      cell: ({ row }: { row: { original: IPost } }) => (
        <p className="text-sm text-gray-700">
          {row.original.state}, {row.original.country}
        </p>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }: { row: { original: IPost } }) => (
        <span
          className={`px-2 py-1 rounded-full text-sm text-white ${
            row.original.status === "open" ? "bg-emerald-500" : "bg-rose-500"
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: IPost } }) => (
        <div className="relative">
          <button
            onClick={() => toggleMenu(row.original._id?.toString() || "")}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <LuEllipsisVerticalIcon className="text-gray-600 text-lg" />
          </button>

          {openMenu === row.original._id?.toString() && (
            <div
              id={`menu-${row.original._id}`}
              className="absolute right-0 mt-2 w-32 bg-white shadow-lg rounded-md z-10"
            >
              <button
                onClick={() => handleEdit(row.original._id)}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
              >
                View
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  const LuDownloadIcon = LuDownload as React.ElementType;
  const LuEllipsisVerticalIcon = LuEllipsisVertical as React.ElementType;

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );

  if (error)
    return (
      <p className="text-rose-500 text-sm">Error loading project posts.</p>
    );

  return (
    <div className="">
      <div className="px-2 py-4 md:px-4 md:py-2 flex items-center justify-between">
        <h2 className="text-xl">Projects</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadCSV}
            className="px-4 py-2 bg-transparent border flex items-center space-x-2 text-gray-800 text-sm rounded-full hover:opacity-50 cursor-pointer transition"
          >
            <LuDownloadIcon />
            <span>CSV</span>
          </button>
        </div>
      </div>

      <Table columns={columns} data={projects} />
      {selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
};

export default Projects;
